import { useEffect, useRef, CSSProperties } from "react";

type VideoProps = {
    autoplay?: boolean;
    loop?: boolean;
    controls?: boolean;
    muted?: boolean;
    poster?: string;
    width?: number;
    height?: number;
    sources?: string[];
    children: React.ReactNode;
};

export const Video = ({
    autoplay,
    loop,
    controls,
    muted,
    poster,
    width,
    height,
    sources,
    children
}: VideoProps) => {
    //ref for autoplay
    const ref = useRef<HTMLVideoElement | null>(null);

    //set scroll handler
    useEffect(() => {
        //decide whether to play at current scroll
        const checkAutoPlay = () => {
            if (!autoplay || !ref?.current) return;

            //don't play it twice
            if (ref.current.played.length) return;

            //get vertical offset where half of video is showing
            const clientRect = ref.current.getBoundingClientRect();
            const startPlayingAt = window.innerHeight - clientRect.height / 2;

            //play if offset is greater than current scroll
            if (clientRect.top < startPlayingAt) {
                ref.current.play().catch((error) => console.warn(error));
            }
        };

        //fire on render in case
        checkAutoPlay();

        //add event listener
        window.addEventListener("scroll", checkAutoPlay, { passive: true });

        //clean up event listener when unmounting
        return () => {
            window.removeEventListener("scroll", checkAutoPlay);
        };
    }, [ref, autoplay]);

    //return video tag
    const videoStyle: CSSProperties = {
        display: "block",
        height: "auto",
        width: "100%"
    };

    return (
        <video
            style={videoStyle}
            data-testid="ui-video"
            ref={ref}
            {...{ loop, controls, muted, poster, width, height }}
        >
            {sources?.map((source) => (
                <source key={source} src={source} type={getVideoType(source)} />
            ))}
            {children}
        </video>
    );
};

//get type property of video by url, eg video/mp4
function getVideoType(url: string): string | undefined {
    const fileName = url.split("/").pop();
    const fileExtension = fileName?.split(".").pop();
    if (fileExtension) {
        if (["mp4"].includes(fileExtension)) return "video/mp4";
        if (["webm"].includes(fileExtension)) return "video/webm";
        if (["ogg", "ogv"].includes(fileExtension)) return "video/ogg";
    }
    return undefined;
}
